import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Sun, Moon, ArrowLeft, BarChart2 } from 'lucide-react';
import { StatsGrid } from '../components/analytics/StatsGrid';
import { BeneficiaryChart } from '../components/analytics/BeneficiaryChart';
import { MoodMonitorCard } from '../components/analytics/MoodMonitorCard';
import { CommunityComparisonCard } from '../components/analytics/CommunityComparisonCard';

export const AnalyticsPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [wellbeingSurveys, setWellbeingSurveys] = useState([]);
  const [communityData, setCommunityData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSurveys = useCallback(async () => {
    try {
      const res = await api.get('/surveys/wellbeing');
      setWellbeingSurveys(res.data);
    } catch (err) { console.error('Failed to refresh wellbeing surveys:', err); }
  }, []);

  const fetchCommunityData = useCallback(async () => {
    try {
      const communityRes = await api.get('/analytics/community-comparison');
      setCommunityData(communityRes.data);
    } catch { setCommunityData(null); }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, weeklyRes, surveysRes] = await Promise.all([
          api.get('/analytics/summary'),
          api.get('/analytics/weekly-summary'),
          api.get('/surveys/wellbeing')
        ]);
        setSummary(summaryRes.data);
        setWeeklySummary(weeklyRes.data);
        setWellbeingSurveys(surveysRes.data);
      } catch (err) { console.error('Failed to load analytics data:', err); }
      finally { setLoading(false); }

      await fetchCommunityData();
    };
    fetchData();
  }, [fetchCommunityData]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <BarChart2 className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold font-['Manrope']">Analytics</span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </header>

      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading analytics...</div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {weeklySummary && (
              <Card className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-200 dark:border-emerald-800">
                <CardContent className="pt-6">
                  <p className="text-lg font-medium">{weeklySummary.message}</p>
                </CardContent>
              </Card>
            )}

            <Card className="glass border-white/20 dark:border-white/10">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg">Achievements To Date</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-3xl font-bold text-emerald-500">{summary?.achievements_this_week || 0}</p>
                    <p className="text-sm text-muted-foreground">This Week</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-blue-500">{summary?.achievements_this_month || 0}</p>
                    <p className="text-sm text-muted-foreground">This Month</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-purple-500">{summary?.achievements_this_year || 0}</p>
                    <p className="text-sm text-muted-foreground">This Year</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <StatsGrid summary={summary} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <BeneficiaryChart summary={summary} />
              <MoodMonitorCard wellbeingSurveys={wellbeingSurveys} onSurveysRefresh={refreshSurveys} />
            </div>

            <CommunityComparisonCard
              communityData={communityData}
              optedIn={user?.compare_stats_opted_in}
              onOptInChange={(opted) => { if (opted) fetchCommunityData(); else setCommunityData(null); }}
            />
          </motion.div>
        )}
      </main>
    </div>
  );
};
